import { FC, RefObject } from "react";

import { Stack } from "@mui/material";
import { hasMultipleLocales, Locales, OrganizationCountry } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { NewsletterFormQuestion, NewsletterFormSignupData } from "./types";

import { newsletterFieldLabelsSx } from "./helpers/fieldStyles";
import { getAnswerableQuestions } from "./helpers/questionAnswers";
import { NewsletterFormAddressFields } from "./NewsletterFormFields/NewsletterFormAddressFields";
import { NewsletterFormCommunicationPreferences } from "./NewsletterFormFields/NewsletterFormCommunicationPreferences";
import { NewsletterFormEmailField } from "./NewsletterFormFields/NewsletterFormEmailField";
import { NewsletterFormLanguageField } from "./NewsletterFormFields/NewsletterFormLanguageField";
import { NewsletterFormNameFields } from "./NewsletterFormFields/NewsletterFormNameFields";
import { NewsletterFormPhoneField } from "./NewsletterFormFields/NewsletterFormPhoneField";
import { NewsletterQuestionField } from "./NewsletterFormFields/NewsletterQuestionField";

import { PhoneNumberInputHandle } from "@/dashboard-design-system/PhoneNumberInput/types";

type NewsletterFormFieldsProps = {
  form: UseFormReturn<NewsletterFormSignupData>;
  organization: { name: string; country: OrganizationCountry; region?: string | null };
  previewConfiguration?: {
    isPreview: boolean;
  };
  hasFirstName?: boolean;
  hasLastName?: boolean;
  hasPhoneNumber?: boolean;
  hasAddress?: boolean;
  hasLanguage?: boolean;
  hasCommunicationPreferences?: boolean;
  isLegacyCommunicationPreferences?: boolean;
  communicationPreferencesTitle?: string | null;
  communicationPreferences?: Array<{
    id: string;
    label: string;
    contactListId: string;
  }>;
  phoneNumberInputRef?: RefObject<PhoneNumberInputHandle | null>;
  phoneInputKey?: number;
  questions?: NewsletterFormQuestion[];
  locale: Locales;
};

export const NewsletterFormFields: FC<NewsletterFormFieldsProps> = ({
  form,
  organization,
  previewConfiguration,
  hasFirstName,
  hasLastName,
  hasPhoneNumber,
  hasAddress,
  hasLanguage,
  hasCommunicationPreferences,
  isLegacyCommunicationPreferences,
  communicationPreferencesTitle,
  communicationPreferences = [],
  phoneNumberInputRef,
  phoneInputKey,
  questions = [],
  locale,
}) => {
  return (
    <Stack gap={1} sx={newsletterFieldLabelsSx}>
      {/* Name fields - only renders if at least one is enabled */}
      {(hasFirstName || hasLastName) && (
        <NewsletterFormNameFields
          form={form}
          hasFirstName={hasFirstName}
          hasLastName={hasLastName}
          previewConfiguration={previewConfiguration}
        />
      )}

      {/* Email - always shown */}
      <NewsletterFormEmailField form={form} previewConfiguration={previewConfiguration} />

      {/* Phone Number - conditional */}
      {hasPhoneNumber && (
        <NewsletterFormPhoneField
          key={phoneInputKey}
          form={form}
          organization={organization}
          previewConfiguration={previewConfiguration}
          phoneNumberInputRef={phoneNumberInputRef}
        />
      )}

      {/* Address fields - conditional */}
      {hasAddress && (
        <NewsletterFormAddressFields
          form={form}
          organization={organization}
          previewConfiguration={previewConfiguration}
        />
      )}

      {/* Language selector - conditional */}
      {hasLanguage && hasMultipleLocales(organization.country) && (
        <NewsletterFormLanguageField form={form} previewConfiguration={previewConfiguration} />
      )}

      {/* Index must match the answers array, so both sides order through getAnswerableQuestions */}
      {getAnswerableQuestions(questions).map((question, index) => (
        <NewsletterQuestionField key={question.id} form={form} question={question} index={index} locale={locale} />
      ))}

      {/* Last on the form, directly above the submit button */}
      {hasCommunicationPreferences && communicationPreferences.length > 0 && (
        <NewsletterFormCommunicationPreferences
          form={form}
          communicationPreferences={communicationPreferences}
          isLegacyCommunicationPreferences={isLegacyCommunicationPreferences}
          communicationPreferencesTitle={communicationPreferencesTitle}
          organizationName={organization.name}
          previewConfiguration={previewConfiguration}
        />
      )}
    </Stack>
  );
};
